import { forwardRef } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export type GWPSelectOption<T extends string | number> = {
	value: T;
	label: string | number;
	disabled?: boolean;
};

export interface GWPSelectProps<T extends string | number>
	extends Omit<
		React.SelectHTMLAttributes<HTMLSelectElement>,
		'size' | 'value' | 'defaultValue' | 'children'
	> {
	options: ReadonlyArray<GWPSelectOption<T>>;
	size?: 'sm' | 'md' | 'lg';
	invalid?: boolean;
	placeholder?: string;
	value?: T | null;
	defaultValue?: T | null;
	onValueChange?: (value: T | null, event: React.ChangeEvent<HTMLSelectElement>) => void;
}

type GWPSelectComponent = <T extends string | number>(
	props: GWPSelectProps<T> & React.RefAttributes<HTMLSelectElement>,
) => React.ReactElement | null;

const getSerializedValue = (value: string | number | null | undefined) => {
	if (value === null || value === undefined) {
		return '';
	}

	return String(value);
};

const GWPSelectInner = forwardRef(function GWPSelectInner<T extends string | number>(
	props: GWPSelectProps<T>,
	ref: React.ForwardedRef<HTMLSelectElement>,
) {
	const {
		className,
		size = 'md',
		invalid = false,
		placeholder,
		options,
		onChange,
		onValueChange,
		disabled,
		value,
		defaultValue,
		...rest
	} = props;

	const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		onChange?.(event);

		const nextValue = event.target.value;
		if (nextValue === '') {
			onValueChange?.(null, event);
			return;
		}

		const selectedOption = options.find((option) => getSerializedValue(option.value) === nextValue);
		onValueChange?.(selectedOption?.value ?? null, event);
	};

	const rootClassName = combineClassNames(
		'gwp-select',
		size,
		invalid ? 'invalid' : undefined,
		disabled ? 'disabled' : undefined,
		className,
	);

	const controlProps =
		value !== undefined
			? { value: getSerializedValue(value) }
			: defaultValue !== undefined
				? { defaultValue: getSerializedValue(defaultValue) }
				: {};

	return (
		<div className={rootClassName}>
			<select
				{...rest}
				{...controlProps}
				ref={ref}
				disabled={disabled}
				aria-invalid={invalid || rest['aria-invalid']}
				className="gwp-select-control"
				onChange={handleChange}
			>
				{placeholder !== undefined && <option value="">{placeholder}</option>}
				{options.map((option) => (
					<option key={getSerializedValue(option.value)} value={getSerializedValue(option.value)} disabled={option.disabled}>
						{option.label}
					</option>
				))}
			</select>
			<span className="gwp-select-indicator" aria-hidden="true" />
		</div>
	);
});

GWPSelectInner.displayName = 'GWPSelect';

const GWPSelect = GWPSelectInner as GWPSelectComponent;

export default GWPSelect;
